/***
 * IMPORTANT
 * This service has a duplicate version in Diary "src/app/services/payment/payment.service.ts"
 * that also needs to be maintained with every change!
 */
import { from, Observable, of } from 'rxjs';
import MenuOptionsService from 'external-services/menu-options-service/index';
import moment from 'moment';
import {
  IAdditionalPaymentDetails,
  IBooking,
  IBookingUnsaved,
  ICalculatePaymentResult,
  IPaymentDetailsBase,
  IResponse,
  PricingTypes,
  servicePaymentType,
  BookingMethods,
  IBookingMenuOption,
  ISavedBookingMenuOption,
  StatusPendingPayment,
  ToastType,
  IInfoAtTime, IToastActionClean,
} from 'shared-types/index';
import { cloneDeep } from 'lodash-es';
import { catchError, first, map } from 'rxjs/operators';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import { appURLS } from 'appRoot/app.constants';
import DateUtilsService from 'external-services/date-utils-service';
import BookingLockService from 'external-services/booking-lock-service';
const NS = 'PaymentService';
const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();
class PaymentService {
  ToastActions: IToastActionClean;

  setup(ToastActions: IToastActionClean) {
    this.ToastActions = ToastActions;
  }

  /**
   * Fetches payment type, taking into account booking options and service payment. Back end does calculations, taking
   * into account pax thresholds on services and booking options.
   */
  getPaymentTypesPerBookingOption(
    opts: IBookingMenuOption[],
    serviceId: string,
    selectedTime: Date | { time: Date },
    bookingTimes: { time: Date }[],
    covers: number,
    bypassValidation = false
  ): Observable<IResponse<ICalculatePaymentResult>> {
    const filteredTimes: { time: Date }[] = bookingTimes;
    const venueId = AdminService.getVenue().id;
    opts = opts.filter((opt) => opt.quantity > 0);

    const optsFlat = MenuOptionsService.getFlatExtras(opts);

    const selectedTimeAsDate: Date =
      selectedTime && (selectedTime instanceof Date ? selectedTime : selectedTime.time);
    const utcTimeAsDate = selectedTimeAsDate || filteredTimes[0].time; // if no time is selected, then we just take the first available time
    const utcTime = moment(utcTimeAsDate, 'YYYY-M-D').utc(true).format('YYYY-MM-DD[T]HH:mm:ss');

    return this.getPaymentType(venueId, optsFlat, serviceId, utcTime, covers, bypassValidation);
  }

  /**
   * Fetches payment type and prices from back end for service and booking options and returns as IPaymentDetailsBase object
   */
  getBookingPaymentDetailsFromServiceAndBookingOptions(
    opts: IBookingMenuOption[],
    serviceId: string,
    selectedTime: any,
    bookingTimes: IInfoAtTime[],
    covers: number,
    bypassValidation: boolean
  ): Observable<{ paymentType: servicePaymentType; price: number }> {
    if (!opts.length) {
      return of({
        paymentType: servicePaymentType.noPayment,
        price: 0,
      });
    }

    return this.getPaymentTypesPerBookingOption(
      opts,
      serviceId,
      selectedTime,
      bookingTimes,
      covers,
      bypassValidation
    ).pipe(
      first(),
      map((result) => {
        const data = result?.data || null;
        return {
          paymentType: data ? data.paymentType : servicePaymentType.noPayment,
          price: data ? Number(Number(data.amount).toFixed(2)) : 0,
        };
      }),
      catchError((err) => {
        console.warn(NS, 'payment call failed', err);
        return of(null);
      })
    );
  }

  getPaymentDetailsByService(
    currentCoversOnBooking: number,
    servicePaymentDetails: IPaymentDetailsBase,
    additionalPaymentDetails: IAdditionalPaymentDetails[],
    bookingDate: Date
  ): IPaymentDetailsBase {
    // temp BO payment details
    if (additionalPaymentDetails && additionalPaymentDetails.length > 0) {
      const tempPaymentDetails = this.getTemporaryBookingOption(
        additionalPaymentDetails,
        bookingDate
      )[0]; // Temporary booking options has higher priority
      // for temp BOs only, if pax does not meet threshold, empty both upsell and standard BOs
      if (tempPaymentDetails && currentCoversOnBooking >= tempPaymentDetails.peopleRequired) {
        return tempPaymentDetails;
      }
    }

    // main service's payment details
    servicePaymentDetails = cloneDeep(servicePaymentDetails); // don't want to affect the original

    const containsStandardBOs: boolean = !!servicePaymentDetails.menuOptions?.length;
    const standardBOsMeetPaxThreshold =
      containsStandardBOs && currentCoversOnBooking >= servicePaymentDetails.peopleRequired;
    if (!standardBOsMeetPaxThreshold) {
      servicePaymentDetails.menuOptions = []; // For standard BOs, if pax does not meet threshold, empty them
    }
    return servicePaymentDetails;
  }
  getPaymentDetailsByServiceIgnorePax(
    currentCoversOnBooking: number,
    servicePaymentDetails: IPaymentDetailsBase,
    additionalPaymentDetails: IAdditionalPaymentDetails[],
    bookingDate: Date
  ): IPaymentDetailsBase {
    // temp BO payment details
    if (additionalPaymentDetails && additionalPaymentDetails.length > 0) {
      const tempPaymentDetails = this.getTemporaryBookingOption(
        additionalPaymentDetails,
        bookingDate
      )[0]; // Temporary booking options has higher priority
      // for temp BOs only, if pax does not meet threshold, empty both upsell and standard BOs
      if (tempPaymentDetails && currentCoversOnBooking >= tempPaymentDetails.peopleRequired) {
        return tempPaymentDetails;
      }
    }

    // main service's payment details
    servicePaymentDetails = cloneDeep(servicePaymentDetails); // don't want to affect the original
    // todo comment follow as DM need show option even it's not meet threshold, need check how to handle this part in Diary
    // const containsStandardBOs: boolean = !!servicePaymentDetails.menuOptions?.length;
    // const standardBOsMeetPaxThreshold =
    //   containsStandardBOs && currentCoversOnBooking >= servicePaymentDetails.peopleRequired;
    // if (!standardBOsMeetPaxThreshold) {
    //   servicePaymentDetails.menuOptions = []; // For standard BOs, if pax does not meet threshold, empty them
    // }
    return servicePaymentDetails;
  }
  /**
   * when first loading a booking from widget, the paymentDetails will be undefined,
   * so here we return the paymentSummary instead
   */
  getPaymentDetailsFromWidgetBooking(booking: IBookingUnsaved): Partial<IPaymentDetailsBase> {
    if (!booking.paymentDetails && booking.paymentSummary) {
      const newPaymentDetails: Partial<IPaymentDetailsBase> = cloneDeep(booking.paymentSummary);
      newPaymentDetails.price =
        booking.paymentSummary.amount - (booking.paymentPending?.discountAmount || 0);
      return newPaymentDetails;
    }
    return null;
  }

  /**
   * Returns the paymentDetails of a booking, with null when free
   */
  getPaymentDetailsByBooking(booking: IBookingUnsaved): Partial<IPaymentDetailsBase> {
    const paymentViaWidget = this.getPaymentDetailsFromWidgetBooking(booking);
    if (paymentViaWidget) {
      return paymentViaWidget;
    }
    const hasPayment =
      booking.paymentDetails && booking.paymentDetails.paymentType !== servicePaymentType.noPayment;
    if (hasPayment) {
      return cloneDeep(booking.paymentDetails);
    }
    return null;
  }

  // returns true if a booking has no payment
  getBookingHasNoPayment(booking: IBooking, checkForWidgetBooking: boolean): boolean {
    let paymentDetails: Partial<IPaymentDetailsBase>;
    if (checkForWidgetBooking) {
      paymentDetails = this.getPaymentDetailsFromWidgetBooking(booking);
    }
    if (!paymentDetails) {
      paymentDetails = booking.paymentDetails;
    }
    return (
      !paymentDetails ||
      !paymentDetails.paymentType ||
      paymentDetails.paymentType === servicePaymentType.noPayment
    );
  }

  populateDefaultPricingTypes(
    servicePaymentDetails: IPaymentDetailsBase,
    additionalPaymentDetails: IAdditionalPaymentDetails[]
  ): void {
    if (servicePaymentDetails && typeof servicePaymentDetails.pricingType === 'undefined') {
      servicePaymentDetails.pricingType = PricingTypes.PerPax;
    }
    if (additionalPaymentDetails?.length) {
      additionalPaymentDetails.forEach((details) => {
        if (typeof details.pricingType === 'undefined') {
          details.pricingType = PricingTypes.PerPax;
        }
      });
    }
  }

  setPaymentDetailsOnBooking(
    booking: IBookingUnsaved,
    activePaymentDetails: IPaymentDetailsBase
  ): IBookingUnsaved {
    const serviceHasBOs =
      activePaymentDetails.menuOptions && !!activePaymentDetails.menuOptions.length;
    if (serviceHasBOs) {
      // if there are no upsell or standard BOs selected, then we can set to no payment
      if (!booking.selectedMenuOptions || !booking.selectedMenuOptions.length) {
        booking.paymentDetails = {
          paymentType: servicePaymentType.noPayment,
        };
      }
    } else {
      // if there are no booking options on service, then the service must be either noPayment or paid
      const isPaxThresholdValid = activePaymentDetails.peopleRequired <= booking.people;
      if (activePaymentDetails.price && isPaxThresholdValid) {
        booking.paymentDetails = {
          paymentType: activePaymentDetails.paymentType,
          price:
            activePaymentDetails.pricingType === PricingTypes.PerBooking
              ? activePaymentDetails.price
              : Number.parseFloat((activePaymentDetails.price * booking.people).toFixed(2)),
          pricingType: activePaymentDetails.pricingType,
        };
      } else {
        booking.paymentDetails = {
          paymentType: servicePaymentType.noPayment,
        };
      }
    }
    return booking;
  }

  isPriceAndBoAvailable(activePaymentDetails: IPaymentDetailsBase): boolean {
    return activePaymentDetails.price > 0 && activePaymentDetails.menuOptions?.length > 0;
  }

  private getTemporaryBookingOption(
    additionalPaymentDetails: IAdditionalPaymentDetails[],
    bookingDate: Date
  ): IAdditionalPaymentDetails[] {
    return additionalPaymentDetails.filter((tempBookingOption) => {
      const optionsFrom = DateUtilsService.dateTimeStrToDate(
        tempBookingOption.optionsFrom
          ? tempBookingOption.optionsFrom
          : moment(new Date()).format('YYYY/MM/DD HH:mm')
      );
      const optionsTo = DateUtilsService.dateTimeStrToDate(
        tempBookingOption.optionsTo
          ? tempBookingOption.optionsTo
          : moment(bookingDate).format('YYYY/MM/DD HH:mm')
      );
      return (
        moment(bookingDate).isSameOrAfter(optionsFrom, 'date') &&
        moment(bookingDate).isSameOrBefore(optionsTo, 'date')
      );
    });
  }

  /**
   * Sets the active payment details, but
   */
  setActivePayments(
    paymentDetails: IPaymentDetailsBase,
    additionalPaymentDetails: IAdditionalPaymentDetails[],
    people: number,
    time: Date
  ): IPaymentDetailsBase {
    let _activePaymentDetails = null;
    if (paymentDetails) {
      this.populateDefaultPricingTypes(paymentDetails, additionalPaymentDetails);
      _activePaymentDetails = this.getPaymentDetailsByService(
        people,
        paymentDetails,
        additionalPaymentDetails,
        time
      );
    }
    console.log('paymentDetails', paymentDetails);
    console.log('_activePaymentDetails', _activePaymentDetails);
    return _activePaymentDetails;
  }
  getPaymentType(
    venueId: number,
    selectedMenuOptions: ISavedBookingMenuOption[],
    serviceId: string,
    bookingDateTime: string,
    covers: number,
    bypassValidation = false
  ): Observable<IResponse<ICalculatePaymentResult>> {
    const data = {
      serviceId,
      time: bookingDateTime,
      numberOfPeople: covers,
      selectedMenuOptions,
      bypassValidation
    }
    return from(api.post(appURLS.API + `/bookings/payments/venues/${venueId}/calculate-payment`, data) as Promise<IResponse<ICalculatePaymentResult>>);
  }

  bookingHasUnpaidPayment(savedBooking: IBooking): boolean {
    if (!savedBooking) {
      return false;
    }
    return (
      savedBooking.paymentDetails &&
      savedBooking.paymentDetails?.paymentType !== servicePaymentType.noPayment &&
      (!savedBooking.paymentSummary || !savedBooking.paymentSummary?.transactionId)
    );
  }

  conditionallyShowLockedBookingDialog(booking: IBooking): boolean {
    const bookingLockResult = BookingLockService.checkLockedPendingPayment(booking);
    if (bookingLockResult.isLocked) {
      this.ToastActions.showToast(bookingLockResult.reason, ToastType.error);
      setTimeout(() => this.ToastActions.clearToast(), 3000);
      return true;
    }
    return false;
  }
}

const instance = new PaymentService();
export default instance as PaymentService;
